import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import Login from '@/pages/Login';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import Dashboard from '@/pages/Dashboard';
import RoomManagement from '@/pages/RoomManagement';
import GuestManagement from '@/pages/GuestManagement';
import BookingManagement from '@/pages/BookingManagement';
import RestaurantPOS from '@/pages/RestaurantPOS';
import RestaurantManagement from '@/pages/RestaurantManagement';
import RestaurantMenu from '@/pages/RestaurantMenu';
import RestaurantTables from '@/pages/RestaurantTables';
import MenuManagement from '@/pages/MenuManagement';
import MenuCategoryManagement from '@/pages/MenuCategoryManagement';
import ServiceManagement from '@/pages/ServiceManagement';
import ServiceOrders from '@/pages/ServiceOrders';
import InvoiceManagement from '@/pages/InvoiceManagement';
import UserManagement from '@/pages/UserManagement';
import BillingCheckout from '@/pages/BillingCheckout';
import SuperAdminDashboard from '@/pages/SuperAdminDashboard';
import SuperAdminOverview from '@/pages/SuperAdminOverview';
import SuperAdminUserManagement from '@/pages/SuperAdminUserManagement';
import SuperAdminMasterData from '@/pages/SuperAdminMasterData';
import CreateHotel from '@/pages/CreateHotel';
import HotelDetails from '@/pages/HotelDetails';
import RoleManagement from '@/pages/RoleManagement';
import TableManagement from '@/pages/TableManagement';
import AmenityManagement from '@/pages/AmenityManagement';
import IDTypeManagement from '@/pages/IDTypeManagement';
import BedTypeManagement from '@/pages/BedTypeManagement';
import POSSettings from '@/pages/POSSettings';
import ThemeSettings from '@/pages/ThemeSettings';
import AssetManagement from '@/pages/AssetManagement';
import SupportTickets from '@/pages/SupportTickets';
import ReportsPage from '@/pages/ReportsPage';
import PaymentSettings from '@/pages/PaymentSettings';
import CreditAccounts from '@/pages/CreditAccounts';
import WhatsAppSettings from '@/pages/WhatsAppSettings';
import TableOrder from '@/pages/TableOrder';
import KitchenDisplay from '@/pages/KitchenDisplay';
import KitchenDisplayPopup from '@/pages/KitchenDisplayPopup';
import KDSSettings from '@/pages/KDSSettings';
import CloudflareR2Settings from '@/pages/CloudflareR2Settings';
import OrderHistory from '@/pages/OrderHistory';
import SelfOrderPage from '@/pages/SelfOrderPage';
import SelfCheckInPage from '@/pages/SelfCheckInPage';
import SelfCheckInLanding from '@/pages/SelfCheckInLanding';
import GuestPortal from '@/pages/GuestPortal';
import CommunicationSettings from '@/pages/CommunicationSettings';
import FinanceDashboard from '@/pages/FinanceDashboard';
import FinanceSetup from '@/pages/FinanceSetup';
import LedgerManagement from '@/pages/LedgerManagement';
import JournalEntryPage from '@/pages/JournalEntryPage';
import FinancialReports from '@/pages/FinancialReports';
import GSTReports from '@/pages/GSTReports';
import CompanyManagement from '@/pages/CompanyManagement';
import PendingPostings from '@/pages/PendingPostings';
import CompanyUsers from '@/pages/CompanyUsers';
import CompanySetup from '@/pages/CompanySetup';
import PurchaseManagement from '@/pages/PurchaseManagement';
import InventoryManagement from '@/pages/InventoryManagement';
import RolesManagement from '@/pages/RolesManagement';
// New standalone pages for Purchase & Inventory
import VendorsPage from '@/pages/VendorsPage';
import PurchaseRequisitionsPage from '@/pages/PurchaseRequisitionsPage';
import PurchaseOrdersPage from '@/pages/PurchaseOrdersPage';
import GRNPage from '@/pages/GRNPage';
import PurchaseInvoicesPage from '@/pages/PurchaseInvoicesPage';
import StoresPage from '@/pages/StoresPage';
import ItemMasterPage from '@/pages/ItemMasterPage';
import StockMovementsPage from '@/pages/StockMovementsPage';
import BudgetDashboard from '@/pages/BudgetDashboard';
import BudgetSetup from '@/pages/BudgetSetup';
import BudgetAllocations from '@/pages/BudgetAllocations';
import KIOTIntegration from '@/pages/KIOTIntegration';
import EmployeeManagement from '@/pages/EmployeeManagement';
import LiveSessions from '@/pages/LiveSessions';
import ServiceStaffDashboard from '@/pages/ServiceStaffDashboard';
import HouseKeepingDashboard from '@/pages/HouseKeepingDashboard';
import HousekeepingManagement from '@/pages/HousekeepingManagement';
import FrontOfficeDashboard from '@/pages/FrontOfficeDashboard';
import OrdersDashboard from '@/pages/OrdersDashboard';
import RoomAvailability from '@/pages/RoomAvailability';
import RoleBasedDashboard from '@/pages/RoleBasedDashboard';
// Public Portal Admin Pages
import PortalSettings from '@/pages/PortalSettings';
import PropertyListing from '@/pages/PropertyListing';
import PropertyConfigurePage from '@/pages/PropertyConfigurePage';
import PhoneSettings from '@/pages/PhoneSettings';
import PBXManagement from '@/pages/PBXManagement';
import AuditTrail from '@/pages/AuditTrail';
import LockSettings from '@/pages/LockSettings';
import PortalUsers from '@/pages/PortalUsers';
import PropertyRegistrations from '@/pages/PropertyRegistrations';
import AmenitiesMasterPage from '@/pages/AmenitiesMasterPage';
import RoomAmenitiesPage from '@/pages/RoomAmenitiesPage';
// Public Booking Portal Pages
import BookingPortalHome from '@/pages/portal/BookingPortalHome';
import BookingPortalSearch from '@/pages/portal/BookingPortalSearch';
import BookingPortalProperty from '@/pages/portal/BookingPortalProperty';
import BookingConfirmed from '@/pages/portal/BookingConfirmed';
import PortalLogin from '@/pages/portal/PortalLogin';
import PortalSignup from '@/pages/portal/PortalSignup';
import MyBookings from '@/pages/portal/MyBookings';
import ListPropertyPage from '@/pages/portal/ListPropertyPage';
import ListPropertyLanding from '@/pages/portal/ListPropertyLanding';
import Layout from '@/components/Layout';
import { ConfirmationProvider } from '@/components/ui/confirmation-dialog';
import { TabsProvider, clearAllTabsOnLogout } from '@/context/TabsContext';
import '@/App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
});

// POS Route Wrapper - checks popup mode inside router context
function POSRouteWrapper({ user, onLogout, activeHotel, onHotelChange, selectedCompany, onCompanyChange, selectedFinancialYear, onFinancialYearChange }) {
  const [searchParams] = useSearchParams();
  const isPopup = searchParams.get('popup') === 'true';
  
  if (isPopup) {
    return <RestaurantPOS user={user} onLogout={onLogout} />;
  }
  
  return (
    <Layout user={user} onLogout={onLogout} activeHotel={activeHotel} onHotelChange={onHotelChange} selectedCompany={selectedCompany} onCompanyChange={onCompanyChange} selectedFinancialYear={selectedFinancialYear} onFinancialYearChange={onFinancialYearChange}>
      <RestaurantPOS user={user} />
    </Layout>
  );
}

// Utility function to extract error message from backend response
export const getErrorMessage = (error, fallback = 'Operation failed') => {
  const detail = error?.response?.data?.detail;
  
  // If detail is a string, return it
  if (typeof detail === 'string') {
    return detail;
  }
  
  // If detail is an array (Pydantic validation errors), format it
  if (Array.isArray(detail) && detail.length > 0) {
    // Try to get the first error message
    const firstError = detail[0];
    if (firstError?.msg) {
      // Include field location if available
      const loc = firstError.loc?.filter(l => l !== 'body')?.join('.') || '';
      return loc ? `${loc}: ${firstError.msg}` : firstError.msg;
    }
    return fallback;
  }
  
  // If detail is an object, try to get a message
  if (detail && typeof detail === 'object') {
    return detail.msg || detail.message || fallback;
  }
  
  return fallback;
};

// Add interceptor to include token AND active hotel ID in all requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Add active hotel ID to all requests (for multi-hotel filtering)
  const activeHotelId = localStorage.getItem('activeHotelId');
  if (activeHotelId) {
    config.headers['X-Hotel-Id'] = activeHotelId;
  }
  
  return config;
});

// Add response interceptor to handle session invalidation (auto-logout when session is killed)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Check if this is a 401 error indicating session was killed
    if (error.response?.status === 401) {
      const detail = error.response?.data?.detail;
      // Only auto-logout if session was invalidated (not just expired token)
      if (detail === 'Invalid token' || detail === 'Session has been terminated' || detail === 'Session expired') {
        // Dispatch custom event for session kill
        window.dispatchEvent(new CustomEvent('session-killed', { detail: { message: detail } }));
      }
    }
    return Promise.reject(error);
  }
);

// Function to apply theme colors to CSS variables AND DOM elements
const applyTheme = (theme) => {
  if (!theme) return;
  
  const root = document.documentElement;
  
  // Apply all colors to CSS custom properties
  if (theme.primary) root.style.setProperty('--theme-primary', theme.primary);
  if (theme.accent) root.style.setProperty('--theme-accent', theme.accent);
  if (theme.background) root.style.setProperty('--theme-background', theme.background);
  if (theme.card_bg) root.style.setProperty('--theme-card-bg', theme.card_bg);
  if (theme.text_primary) root.style.setProperty('--theme-text-primary', theme.text_primary);
  if (theme.text_secondary) root.style.setProperty('--theme-text-secondary', theme.text_secondary);
  if (theme.sidebar_bg) root.style.setProperty('--theme-sidebar-bg', theme.sidebar_bg);
  if (theme.sidebar_text) root.style.setProperty('--theme-sidebar-text', theme.sidebar_text);
  
  // Apply directly to body and main elements
  if (theme.background) {
    document.body.style.backgroundColor = theme.background;
    document.body.style.color = theme.text_primary || '#1f2937';
  }
  
  // Force apply to elements after a short delay to ensure DOM is ready
  const applyToDOM = () => {
    // Main background
    document.querySelectorAll('.bg-gradient-hero, .min-h-screen').forEach(el => {
      if (theme.background) el.style.backgroundColor = theme.background;
    });
    
    // Sidebar
    document.querySelectorAll('aside').forEach(el => {
      if (theme.sidebar_bg) el.style.backgroundColor = theme.sidebar_bg;
    });
    
    // Cards
    document.querySelectorAll('[class*="Card"], .card').forEach(el => {
      if (theme.card_bg) el.style.backgroundColor = theme.card_bg;
    });
    
    // Gradient buttons
    document.querySelectorAll('.bg-gradient-orange').forEach(el => {
      if (theme.primary) {
        el.style.background = `linear-gradient(135deg, ${theme.primary} 0%, ${adjustColor(theme.primary, -20)} 100%)`;
      }
    });
    
    // Text primary
    document.querySelectorAll('.text-gray-800, .text-gray-900, h1, h2, h3').forEach(el => {
      if (theme.text_primary) el.style.color = theme.text_primary;
    });
    
    // Text secondary  
    document.querySelectorAll('.text-gray-500, .text-gray-600').forEach(el => {
      if (theme.text_secondary) el.style.color = theme.text_secondary;
    });
  };
  
  // Apply immediately and after delays
  applyToDOM();
  setTimeout(applyToDOM, 100);
  setTimeout(applyToDOM, 500);
  
  // Store theme in localStorage for persistence
  localStorage.setItem('currentTheme', JSON.stringify(theme));
  
  console.log('Theme applied:', theme.preset || 'custom', theme);
};

// Helper function to darken/lighten a hex color
const adjustColor = (hex, percent) => {
  if (!hex || hex.length < 4) return hex;
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = (num >> 8 & 0x00FF) + amt;
  const B = (num & 0x0000FF) + amt;
  return '#' + (0x1000000 + 
    (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 + 
    (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 + 
    (B < 255 ? (B < 1 ? 0 : B) : 255)
  ).toString(16).slice(1);
};

// Function to load theme from API
const loadAndApplyTheme = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    const activeHotelId = localStorage.getItem('activeHotelId');
    const headers = { Authorization: `Bearer ${token}` };
    if (activeHotelId) {
      headers['X-Hotel-Id'] = activeHotelId;
    }
    
    const response = await axios.get(`${API}/theme`, { headers });
    if (response.data) {
      applyTheme(response.data);
    }
  } catch (error) {
    console.error('Failed to load theme:', error);
    // Try to apply cached theme
    const cachedTheme = localStorage.getItem('currentTheme');
    if (cachedTheme) {
      try {
        applyTheme(JSON.parse(cachedTheme));
      } catch (e) {
        console.error('Failed to parse cached theme');
      }
    }
  }
};

// Immediately apply cached browser title (before React renders) to avoid flash
const cachedBrowserTitle = localStorage.getItem('browserTitle');
if (cachedBrowserTitle) {
  document.title = cachedBrowserTitle;
}

// Function to load and apply browser title from portal settings (works for all users)
const loadBrowserTitle = async () => {
  try {
    // First try public endpoint (doesn't require auth)
    const response = await axios.get(`${API}/portal/settings`);
    if (response.data?.browser_title) {
      document.title = response.data.browser_title;
      localStorage.setItem('browserTitle', response.data.browser_title);
      return;
    }
  } catch (error) {
    console.error('Failed to load browser title from public endpoint:', error);
  }
  
  // Fallback to cached title or default
  const cachedTitle = localStorage.getItem('browserTitle');
  if (cachedTitle) {
    document.title = cachedTitle;
  } else {
    document.title = 'Hotel Management System';
  }
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeHotel, setActiveHotel] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedFinancialYear, setSelectedFinancialYear] = useState(null);

  // Load browser title on app startup (for all pages including public portal)
  useEffect(() => {
    loadBrowserTitle();
  }, []);

  // Handle session kill event (auto-logout when admin kills session)
  useEffect(() => {
    const handleSessionKill = (event) => {
      console.log('Session killed:', event.detail?.message);
      // Clear all tabs first
      clearAllTabsOnLogout();
      // Clear storage
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('activeHotelId');
      localStorage.removeItem('activeHotelName');
      localStorage.removeItem('selectedCompanyId');
      localStorage.removeItem('selectedCompanyName');
      localStorage.removeItem('selectedFinancialYearId');
      localStorage.removeItem('selectedFinancialYearName');
      localStorage.removeItem('currentTheme');
      localStorage.removeItem('lastLogin');
      // Reset state
      setUser(null);
      setActiveHotel(null);
      setSelectedCompany(null);
      setSelectedFinancialYear(null);
      // Show message and redirect to partner portal
      alert('Your session has been terminated by an administrator. Please login again.');
      window.location.href = '/partner';
    };

    window.addEventListener('session-killed', handleSessionKill);
    return () => window.removeEventListener('session-killed', handleSessionKill);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      api.get('/auth/me')
        .then(response => {
          setUser(response.data);
          // Load saved active hotel
          const savedHotelId = localStorage.getItem('activeHotelId');
          const savedHotelName = localStorage.getItem('activeHotelName');
          if (savedHotelId && savedHotelName) {
            setActiveHotel({ id: savedHotelId, name: savedHotelName });
          }
          // Load saved selected company
          const savedCompanyId = localStorage.getItem('selectedCompanyId');
          const savedCompanyName = localStorage.getItem('selectedCompanyName');
          if (savedCompanyId && savedCompanyName) {
            setSelectedCompany({ id: savedCompanyId, name: savedCompanyName });
          }
          // Load saved selected financial year
          const savedFYId = localStorage.getItem('selectedFinancialYearId');
          const savedFYName = localStorage.getItem('selectedFinancialYearName');
          if (savedFYId && savedFYName) {
            setSelectedFinancialYear({ id: savedFYId, name: savedFYName });
          }
          // Load and apply theme
          loadAndApplyTheme();
          // Load browser title for admin users
          if (response.data.role === 'super_admin') {
            loadBrowserTitle();
          }
          setLoading(false);
        })
        .catch(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('activeHotelId');
          localStorage.removeItem('activeHotelName');
          localStorage.removeItem('selectedCompanyId');
          localStorage.removeItem('selectedCompanyName');
          localStorage.removeItem('selectedFinancialYearId');
          localStorage.removeItem('selectedFinancialYearName');
          localStorage.removeItem('currentTheme');
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    // Store user data for tab-based components
    localStorage.setItem('user', JSON.stringify(userData));
    // Store last login time from server response
    if (userData.last_login) {
      localStorage.setItem('lastLogin', userData.last_login);
    }
    // For hotel-level users, auto-set their hotel as active
    if (userData.hotel_id && ['hotel_admin', 'manager', 'front_office'].includes(userData.role)) {
      localStorage.setItem('activeHotelId', userData.hotel_id);
      // Hotel name will be set by TopHeader when it loads
    }
    setUser(userData);
    // Load theme after login
    setTimeout(() => loadAndApplyTheme(), 100);
    // Load browser title for super admin
    if (userData.role === 'super_admin') {
      setTimeout(() => loadBrowserTitle(), 100);
    }
  };

  const handleLogout = async () => {
    // Call logout API to clear server session
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await api.post('/auth/logout');
      }
    } catch (error) {
      console.error('Logout API error:', error);
      // Continue with local logout even if API fails
    }
    
    // Clear all tabs first
    clearAllTabsOnLogout();
    
    // Clear localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('activeHotelId');
    localStorage.removeItem('activeHotelName');
    localStorage.removeItem('selectedCompanyId');
    localStorage.removeItem('selectedCompanyName');
    localStorage.removeItem('selectedFinancialYearId');
    localStorage.removeItem('selectedFinancialYearName');
    localStorage.removeItem('currentTheme');
    localStorage.removeItem('lastLogin');
    
    // Reset theme to default
    document.documentElement.style.removeProperty('--theme-primary');
    document.documentElement.style.removeProperty('--theme-accent');
    document.documentElement.style.removeProperty('--theme-background');
    
    // Redirect to partner portal IMMEDIATELY before React state changes trigger route guards
    window.location.href = '/partner';
  };

  const handleHotelChange = (hotel) => {
    setActiveHotel(hotel);
    // Theme will be reloaded on page refresh
  };

  const handleCompanyChange = (company) => {
    setSelectedCompany(company);
    localStorage.setItem('selectedCompanyId', company.id);
    localStorage.setItem('selectedCompanyName', company.name);
    // Clear financial year when company changes
    setSelectedFinancialYear(null);
    localStorage.removeItem('selectedFinancialYearId');
    localStorage.removeItem('selectedFinancialYearName');
  };

  const handleFinancialYearChange = (fy) => {
    setSelectedFinancialYear(fy);
    localStorage.setItem('selectedFinancialYearId', fy.id);
    localStorage.setItem('selectedFinancialYearName', fy.name || fy.year);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-hero">
        <div className="text-center">
          <div className="h-12 w-12 rounded-full border-4 border-orange-500 border-t-transparent animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <ConfirmationProvider>
    <TabsProvider>
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Public Self-Order Route - No Auth Required */}
          <Route path="/self-order/:sessionCode" element={<SelfOrderPage />} />
          
          {/* Public Self Check-in Routes - No Auth Required */}
          <Route path="/self-checkin" element={<SelfCheckInLanding />} />
          <Route path="/self-checkin/:code" element={<SelfCheckInPage />} />
          
          {/* Public Guest Portal - No Auth Required */}
          <Route path="/guest-portal/:token" element={<GuestPortal />} />
          
          {/* Public Booking Portal Routes - At Root Level */}
          <Route path="/" element={<BookingPortalHome />} />
          <Route path="/search" element={<BookingPortalSearch />} />
          <Route path="/property/:propertyId" element={<BookingPortalProperty />} />
          <Route path="/booking-confirmed" element={<BookingConfirmed />} />
          <Route path="/login" element={<PortalLogin />} />
          <Route path="/signup" element={<PortalSignup />} />
          <Route path="/my-bookings" element={<MyBookings />} />
          <Route path="/list-property" element={<ListPropertyPage />} />
          <Route path="/partner" element={<ListPropertyLanding />} />
          <Route path="/for-hotels" element={<ListPropertyLanding />} />

          {/* Backward compatibility redirects for old /portal/* paths */}
          <Route path="/portal" element={<Navigate to="/" replace />} />
          <Route path="/portal/search" element={<Navigate to="/search" replace />} />
          <Route path="/portal/property/:propertyId" element={<Navigate to="/property/:propertyId" replace />} />
          <Route path="/portal/login" element={<Navigate to="/login" replace />} />
          <Route path="/portal/signup" element={<Navigate to="/signup" replace />} />
          <Route path="/portal/my-bookings" element={<Navigate to="/my-bookings" replace />} />
          
          {/* Admin Login - Redirect to Partner Portal */}
          <Route path="/admin/login" element={<Navigate to="/partner" replace />} />
          <Route path="/admin/forgot-password" element={user ? <Navigate to="/admin" /> : <ForgotPassword />} />
          <Route path="/admin/reset-password" element={user ? <Navigate to="/admin" /> : <ResetPassword />} />
          
          {/* POS Popup Route - No Layout when popup=true */}
          <Route
            path="/admin/restaurant"
            element={
              !user ? <Navigate to="/partner" /> : (
                <POSRouteWrapper user={user} onLogout={handleLogout} activeHotel={activeHotel} onHotelChange={handleHotelChange} selectedCompany={selectedCompany} onCompanyChange={handleCompanyChange} selectedFinancialYear={selectedFinancialYear} onFinancialYearChange={handleFinancialYearChange} />
              )
            }
          />
          
          {/* KDS Popup Route - No Layout */}
          <Route
            path="/admin/kds"
            element={
              !user ? <Navigate to="/partner" /> : (
                <KitchenDisplayPopup user={user} onLogout={handleLogout} />
              )
            }
          />
          
          {/* Table Order Popup Route - Check for popup param */}
          <Route
            path="/admin/table-order"
            element={
              !user ? <Navigate to="/partner" /> : (
                window.location.search.includes('popup=true') ? (
                  <TableOrder user={user} onLogout={handleLogout} />
                ) : (
                  <Layout user={user} onLogout={handleLogout} activeHotel={activeHotel} onHotelChange={handleHotelChange} selectedCompany={selectedCompany} onCompanyChange={handleCompanyChange} selectedFinancialYear={selectedFinancialYear} onFinancialYearChange={handleFinancialYearChange}>
                    <TableOrder user={user} />
                  </Layout>
                )
              )
            }
          />
          
          {/* Service Staff Direct Route - No Layout/Sidebar */}
          <Route
            path="/admin/staff-dashboard"
            element={
              !user ? <Navigate to="/partner" /> : (
                <ServiceStaffDashboard user={user} />
              )
            }
          />
          
          {/* Admin Dashboard Routes - Under /admin */}
          <Route
            path="/admin/*"
            element={
              !user ? <Navigate to="/partner" /> : (
                // Service staff gets redirected to their standalone dashboard
                user.role === 'service_staff' ? <Navigate to="/admin/staff-dashboard" replace /> : (
                <Layout user={user} onLogout={handleLogout} activeHotel={activeHotel} onHotelChange={handleHotelChange} selectedCompany={selectedCompany} onCompanyChange={handleCompanyChange} selectedFinancialYear={selectedFinancialYear} onFinancialYearChange={handleFinancialYearChange}>
                  <Routes>
                    <Route path="/" element={user.role === 'super_admin' ? <SuperAdminOverview /> : <RoleBasedDashboard user={user} activeHotel={activeHotel} />} />
                    <Route path="/super-admin/dashboard" element={<SuperAdminDashboard user={user} />} />
                    <Route path="/super-admin/users" element={<SuperAdminUserManagement />} />
                    <Route path="/super-admin/master-data" element={<SuperAdminMasterData />} />
                    <Route path="/super-admin/hotels/new" element={<CreateHotel user={user} />} />
                    <Route path="/super-admin/hotels/:hotelId" element={<HotelDetails user={user} />} />
                    <Route path="/super-admin/kiot" element={<KIOTIntegration />} />
                    <Route path="/live-sessions" element={<LiveSessions user={user} />} />
                    <Route path="/rooms" element={<RoomManagement user={user} />} />
                    <Route path="/room-availability" element={<RoomAvailability user={user} />} />
                    <Route path="/guests" element={<GuestManagement user={user} />} />
                    <Route path="/bookings" element={<BookingManagement user={user} />} />
                    <Route path="/billing" element={<BillingCheckout user={user} />} />
                    <Route path="/restaurants" element={<RestaurantManagement user={user} />} />
                    <Route path="/restaurants/:restaurantId/menu" element={<RestaurantMenu user={user} />} />
                    <Route path="/restaurants/:restaurantId/tables" element={<RestaurantTables user={user} />} />
                    <Route path="/menu" element={<MenuManagement user={user} />} />
                    <Route path="/menu-categories" element={<MenuCategoryManagement />} />
                    <Route path="/tables" element={<TableManagement user={user} />} />
                    <Route path="/amenities" element={<AmenityManagement />} />
                    <Route path="/amenities-master" element={<AmenitiesMasterPage user={user} />} />
                    <Route path="/room-amenities" element={<RoomAmenitiesPage />} />
                    <Route path="/id-types" element={<IDTypeManagement />} />
                    <Route path="/bed-types" element={<BedTypeManagement />} />
                    <Route path="/services" element={<ServiceManagement user={user} />} />
                    <Route path="/service-orders" element={<ServiceOrders user={user} />} />
                    <Route path="/orders-dashboard" element={<OrdersDashboard user={user} />} />
                    <Route path="/staff-dashboard" element={<ServiceStaffDashboard user={user} />} />
                    <Route path="/housekeeping-dashboard" element={<HouseKeepingDashboard user={user} />} />
                    <Route path="/housekeeping" element={<HousekeepingManagement user={user} activeHotel={activeHotel} />} />
                    <Route path="/kitchen" element={<KitchenDisplay />} />
                    <Route path="/order-history" element={<OrderHistory />} />
                    <Route path="/invoices" element={<InvoiceManagement user={user} />} />
                    <Route path="/settings/roles" element={<RoleManagement user={user} />} />
                    <Route path="/settings/pos" element={<POSSettings />} />
                    <Route path="/settings/kds" element={<KDSSettings />} />
                    <Route path="/settings/theme" element={<ThemeSettings onThemeChange={applyTheme} />} />
                    <Route path="/settings/payment" element={<PaymentSettings />} />
                    <Route path="/settings/whatsapp" element={<WhatsAppSettings />} />
                    <Route path="/settings/communication" element={<CommunicationSettings user={user} />} />
                    <Route path="/settings/roles" element={<RolesManagement user={user} />} />
                    <Route path="/credit-accounts" element={<CreditAccounts />} />
                    <Route path="/assets" element={<AssetManagement />} />
                    <Route path="/vendors" element={<VendorsPage />} />
                    <Route path="/purchase-requisitions" element={<PurchaseRequisitionsPage />} />
                    <Route path="/purchase-orders" element={<PurchaseOrdersPage />} />
                    <Route path="/grn" element={<GRNPage />} />
                    <Route path="/purchase-invoices" element={<PurchaseInvoicesPage />} />
                    <Route path="/stores" element={<StoresPage />} />
                    <Route path="/item-master" element={<ItemMasterPage />} />
                    <Route path="/stock-items" element={<ItemMasterPage />} />
                    <Route path="/stock-movements" element={<StockMovementsPage />} />
                    <Route path="/tickets" element={<SupportTickets user={user} />} />
                    <Route path="/reports" element={<ReportsPage user={user} />} />
                    <Route path="/finance" element={<FinanceDashboard user={user} />} />
                    <Route path="/finance/setup" element={<FinanceSetup user={user} />} />
                    <Route path="/finance/ledgers" element={<LedgerManagement user={user} />} />
                    <Route path="/finance/journal" element={<JournalEntryPage user={user} />} />
                    <Route path="/finance/reports" element={<FinancialReports user={user} />} />
                    <Route path="/finance/reports/:reportType" element={<FinancialReports user={user} />} />
                    <Route path="/finance/gst" element={<GSTReports user={user} />} />
                    <Route path="/finance/postings" element={<PendingPostings user={user} />} />
                    <Route path="/budget" element={<BudgetDashboard user={user} selectedCompany={selectedCompany} />} />
                    <Route path="/budget/setup" element={<BudgetSetup user={user} selectedCompany={selectedCompany} />} />
                    <Route path="/budget/allocations" element={<BudgetAllocations user={user} selectedCompany={selectedCompany} />} />
                    <Route path="/companies" element={<CompanyManagement />} />
                    <Route path="/company/setup" element={<CompanySetup user={user} />} />
                    <Route path="/company/users" element={<CompanyUsers user={user} selectedCompany={selectedCompany} />} />
                    <Route path="/company/finance-setup" element={<FinanceSetup user={user} selectedCompany={selectedCompany} />} />
                    <Route path="/company/employees" element={<EmployeeManagement />} />
                    <Route path="/employees" element={<EmployeeManagement />} />
                    {/* Public Portal Admin Routes */}
                    {user.role === 'super_admin' && (
                      <Route path="/portal/settings" element={<PortalSettings user={user} />} />
                    )}
                    <Route path="/portal/listing" element={<PropertyListing user={user} />} />
                    <Route path="/portal/listing/configure/:propertyId" element={<PropertyConfigurePage user={user} />} />
                    {(user.role === 'super_admin' || user.role === 'company_admin') && (
                      <Route path="/portal/users" element={<PortalUsers user={user} />} />
                    )}
                    {user.role === 'super_admin' && (
                      <Route path="/portal/registrations" element={<PropertyRegistrations />} />
                    )}
                    <Route path="/phone-settings" element={<PhoneSettings user={user} />} />
                    <Route path="/settings/phone" element={<PhoneSettings user={user} />} />
                    <Route path="/settings/locks" element={<LockSettings user={user} />} />
                    {user.role === 'super_admin' && (
                      <>
                        <Route path="/pbx-management" element={<PBXManagement user={user} />} />
                        <Route path="/audit-trail" element={<AuditTrail user={user} />} />
                      </>
                    )}
                    {user.role === 'super_admin' && (
                      <Route path="/storage-settings" element={<CloudflareR2Settings user={user} />} />
                    )}
                    {(user.role === 'super_admin' || user.role === 'hotel_admin' || user.role === 'manager') && (
                      <Route path="/users" element={<UserManagement user={user} />} />
                    )}
                  </Routes>
                </Layout>
                )
              )
            }
          />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
    </TabsProvider>
    </ConfirmationProvider>
  );
}

export default App;
